<template>
  <div class="legal-page">
    <div class="legal-container">
      <!-- Header -->
      <header class="legal-header">
        <div class="header-content">
          <router-link to="/" class="back-link">
            <a-icon type="arrow-left" /> Back to Home
          </router-link>
          <h1>Privacy Policy</h1>
          <p class="last-updated">Last Updated: January 11, 2026</p>
        </div>
      </header>

      <!-- Content -->
      <div class="legal-content">
        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to ZeroCents ("we," "our," or "us"). We are committed to protecting your privacy and ensuring
            the security of your personal information. This Privacy Policy explains how we collect, use, disclose,
            and safeguard your information when you use our financial tracking application and services.
          </p>
          <p>
            By using ZeroCents, you agree to the collection and use of information in accordance with this policy.
            If you do not agree with our policies and practices, please do not use our services.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>

          <h3>2.1 Personal Information</h3>
          <p>When you register for an account, we collect:</p>
          <ul>
            <li>Username</li>
            <li>Email address</li>
            <li>First and last name</li>
            <li>Password (encrypted)</li>
          </ul>

          <h3>2.2 Financial Data</h3>
          <p>When you use our services, you may provide:</p>
          <ul>
            <li>Transaction records (amounts, dates, descriptions, notes)</li>
            <li>Category information</li>
            <li>Balance information</li>
            <li>Receipt images</li>
            <li>Organization information</li>
          </ul>

          <h3>2.3 Technical Information</h3>
          <p>We automatically collect:</p>
          <ul>
            <li>IP address</li>
            <li>Browser type and version</li>
            <li>Device information</li>
            <li>Usage data and analytics</li>
            <li>Log files</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the collected information for:</p>
          <ul>
            <li><strong>Service Provision:</strong> To provide, maintain, and improve our financial tracking services</li>
            <li><strong>Account Management:</strong> To create and manage your user account</li>
            <li><strong>Communication:</strong> To send you important updates, security alerts, and support messages</li>
            <li><strong>Analytics:</strong> To understand how users interact with our services and improve user experience</li>
            <li><strong>Security:</strong> To detect, prevent, and address security issues and fraudulent activity</li>
            <li><strong>Legal Compliance:</strong> To comply with legal obligations and enforce our terms</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your information:
          </p>
          <ul>
            <li><strong>Encryption:</strong> All data transmission is encrypted using SSL/TLS protocols</li>
            <li><strong>Password Protection:</strong> Passwords are hashed using bcrypt encryption</li>
            <li><strong>Access Controls:</strong> Strict access controls limit who can view your data</li>
            <li><strong>Regular Security Audits:</strong> We regularly review and update our security practices</li>
            <li><strong>Secure Infrastructure:</strong> Data is stored on secure servers with backup systems</li>
          </ul>
          <p>
            However, no method of transmission over the Internet or electronic storage is 100% secure.
            While we strive to protect your information, we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>5. Data Sharing and Disclosure</h2>

          <h3>5.1 We Do Not Sell Your Data</h3>
          <p>We do not sell, trade, or rent your personal information to third parties.</p>

          <h3>5.2 Sharing Within Organizations</h3>
          <p>
            Your financial data is shared with members of your organization based on their assigned roles
            (Admin, Member, or Viewer). You control who has access by managing organization membership.
          </p>

          <h3>5.3 Legal Requirements</h3>
          <p>We may disclose your information if required to do so by law or in response to:</p>
          <ul>
            <li>Valid legal processes (subpoenas, court orders)</li>
            <li>Government requests</li>
            <li>Protection of our rights and safety</li>
            <li>Investigation of fraud or security issues</li>
          </ul>

          <h3>5.4 Service Providers</h3>
          <p>
            We may share information with trusted third-party service providers who assist us in
            operating our services (hosting, analytics, email services). These providers are
            contractually obligated to protect your information.
          </p>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>
            We retain your personal information for as long as your account is active or as needed to
            provide you services. You may request deletion of your data at any time by contacting us.
            We will retain and use your information as necessary to comply with legal obligations,
            resolve disputes, and enforce our agreements.
          </p>
        </section>

        <section>
          <h2>7. Your Rights and Choices</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your account and data</li>
            <li><strong>Export:</strong> Export your financial data in a portable format</li>
            <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
            <li><strong>Object:</strong> Object to certain data processing activities</li>
          </ul>
          <p>
            To exercise these rights, please contact us at support@zerocents.com or through your
            account settings.
          </p>
        </section>

        <section>
          <h2>8. Cookies and Tracking</h2>
          <p>
            We use cookies and similar tracking technologies to track activity on our service and
            store certain information. Cookies are files with small amount of data that are sent to
            your browser from a website and stored on your device.
          </p>
          <p>We use cookies for:</p>
          <ul>
            <li>Session management (keeping you logged in)</li>
            <li>Preference storage</li>
            <li>Analytics and performance monitoring</li>
          </ul>
          <p>
            You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            However, if you do not accept cookies, you may not be able to use some portions of our service.
          </p>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            Our service is not intended for users under the age of 13. We do not knowingly collect
            personal information from children under 13. If you are a parent or guardian and believe
            your child has provided us with personal information, please contact us, and we will
            delete such information.
          </p>
        </section>

        <section>
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and maintained on computers located outside of your
            state, province, country, or other governmental jurisdiction where data protection laws may
            differ. By using our service, you consent to such transfers.
          </p>
        </section>

        <section>
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update our Privacy Policy from time to time. We will notify you of any changes by
            posting the new Privacy Policy on this page and updating the "Last Updated" date. You are
            advised to review this Privacy Policy periodically for any changes. Changes are effective
            when posted on this page.
          </p>
        </section>

        <section>
          <h2>12. Contact Us</h2>
          <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
          <ul>
            <li><strong>Email:</strong> support@zerocents.com</li>
            <li><strong>Address:</strong> 123 Finance Street, San Francisco, CA 94105</li>
          </ul>
        </section>

        <section>
          <h2>13. GDPR Compliance (For EU Users)</h2>
          <p>
            If you are located in the European Economic Area (EEA), you have certain data protection
            rights under the General Data Protection Regulation (GDPR):
          </p>
          <ul>
            <li>Right to access your personal data</li>
            <li>Right to rectification of inaccurate data</li>
            <li>Right to erasure ("right to be forgotten")</li>
            <li>Right to restrict processing</li>
            <li>Right to data portability</li>
            <li>Right to object to processing</li>
            <li>Right to withdraw consent</li>
          </ul>
          <p>
            Our legal basis for processing your personal data is your consent, contract performance,
            and our legitimate business interests. To exercise your GDPR rights, contact us at
            support@zerocents.com.
          </p>
        </section>
      </div>

      <!-- Footer Navigation -->
      <div class="legal-footer">
        <router-link to="/" class="footer-link">Home</router-link>
        <router-link to="/terms" class="footer-link">Terms of Service</router-link>
        <a href="mailto:support@zerocents.com" class="footer-link">Contact Us</a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Privacy',
  mounted() {
    window.scrollTo(0, 0);
  }
};
</script>

<style lang="scss" scoped>
.legal-page {
  min-height: 100vh;
  background: #f9fafb;
  padding: 2rem 0;
}

.legal-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
}

.legal-header {
  background: #fff;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.header-content {
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1890ff;
    margin: 1rem 0 0.5rem;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  .last-updated {
    color: #666;
    font-style: italic;
    margin: 0;
  }
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #1890ff;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;

  &:hover {
    color: #40a9ff;
  }
}

.legal-content {
  background: #fff;
  border-radius: 8px;
  padding: 3rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  section {
    margin-bottom: 2.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  h2 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #1890ff;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #555;
    margin: 1.5rem 0 1rem;
  }

  p {
    line-height: 1.8;
    color: #555;
    margin-bottom: 1rem;
  }

  ul {
    margin: 1rem 0;
    padding-left: 2rem;

    li {
      line-height: 1.8;
      color: #555;
      margin-bottom: 0.5rem;

      strong {
        color: #333;
      }
    }
  }
}

.legal-footer {
  display: flex;
  justify-content: center;
  gap: 2rem;
  padding: 1.5rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}

.footer-link {
  color: #1890ff;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;

  &:hover {
    color: #40a9ff;
  }
}
</style>
